import { forwardRef, useMemo } from 'react'
import { EChart, type EChartHandle } from './EChart'
import { buildRankedBarsOption, foldTopN, OTHER_KEY, rankedBarsHeight, type RankedBarItem, type RankedBarsInput } from './rankedBarsOption'
import { useChartTheme } from './useChartTheme'

export interface RankedBarsProps extends RankedBarsInput {
  /** Высота; по умолчанию — по числу строк. */
  height?: number
  loading?: boolean
  ariaLabel?: string
  className?: string
  texture?: boolean
  /** Клик по столбцу (не по «Прочему») — провал. */
  onItemClick?: (item: RankedBarItem) => void
  /** Наведение на столбец: ключ объекта или `null`. */
  onItemHover?: (key: string | null) => void
  /** Подсветить столбец по ключу (наведение на строку таблицы). */
  highlightKey?: string | null
}

/** Горизонтальный рейтинг «топ-N + Прочее» с долей и Δ к сравнению. */
export const RankedBars = forwardRef<EChartHandle, RankedBarsProps>(function RankedBars(
  { height, loading, ariaLabel, className, texture, onItemClick, onItemHover, highlightKey, ...input },
  ref,
) {
  const theme = useChartTheme()
  const rows = useMemo(
    () => foldTopN(input),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [input.items, input.topN, input.total, input.previousTotal, input.otherLabel, input.order, input.showOther],
  )
  const option = useMemo(
    () => buildRankedBarsOption(input, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme, input.items, input.topN, input.total, input.previousTotal, input.otherLabel, input.valueFormat, input.showDelta, input.showShare, input.invertDelta, input.color, input.order, input.showOther, input.markers],
  )
  const highlightIndex = highlightKey ? rows.findIndex((row) => row.key === highlightKey) : -1
  const highlight = useMemo(() => (highlightIndex >= 0 ? { seriesIndex: 0, dataIndex: highlightIndex } : null), [highlightIndex])

  return (
    <EChart
      ref={ref}
      option={option}
      height={height ?? rankedBarsHeight(rows.length)}
      loading={loading}
      ariaLabel={ariaLabel}
      className={className}
      texture={texture}
      highlight={highlight}
      onMarkClick={(params) => {
        if (params.componentType !== 'series' || params.dataIndex === undefined) return
        const item = rows[params.dataIndex]
        if (!item || item.key === OTHER_KEY || item.drillable === false) return
        onItemClick?.(item)
      }}
      onMarkHover={(params) => {
        if (!onItemHover) return
        const item = params?.dataIndex !== undefined ? rows[params.dataIndex] : undefined
        onItemHover(item && item.key !== OTHER_KEY ? item.key : null)
      }}
    />
  )
})

export default RankedBars
